import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import {
  ShieldCheck,
  Clock,
  PhoneCall,
  Play,
  Save,
  Loader2,
  AlertTriangle,
  Building2,
  Mail,
  Phone,
  MapPin,
  Scale,
  ExternalLink,
} from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { LEGAL_COMPANY_INFO } from "../legal/legalConfig";
import {
  useOptionalCRM,
  ACTIVE_CRM_STORAGE_KEY,
  DEFAULT_CRM_ID,
} from "../context/CRMContext";

export const LgpdRetentionSettings = () => {
  const notify = useNotify();
  const crmCtx = useOptionalCRM();
  const activeCrmId =
    crmCtx?.currentCrm?.id ||
    localStorage.getItem(ACTIVE_CRM_STORAGE_KEY) ||
    DEFAULT_CRM_ID;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningRoutine, setRunningRoutine] = useState(false);

  const [accessLogsDays, setAccessLogsDays] = useState("180");
  const [callRecordingsDays, setCallRecordingsDays] = useState("");
  const [transcriptsDays, setTranscriptsDays] = useState("");
  const [inactiveLeadsDays, setInactiveLeadsDays] = useState("");

  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [recordingNoticeConfigured, setRecordingNoticeConfigured] =
    useState(false);
  const [lastReport, setLastReport] = useState<any>(null);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        const client = getSupabaseClient();
        const { data: crmRow } = await client
          .from("crms")
          .select("id, settings")
          .eq("id", activeCrmId)
          .maybeSingle();

        const s = crmRow?.settings || {};
        const r = s.retention || {};
        const lgpd = s.lgpd || {};

        setAccessLogsDays(
          String(Math.max(180, Number(r.access_and_audit_logs_days) || 180)),
        );
        setCallRecordingsDays(
          r.call_recordings_days ? String(r.call_recordings_days) : "",
        );
        setTranscriptsDays(
          r.call_transcripts_and_notes_days
            ? String(r.call_transcripts_and_notes_days)
            : "",
        );
        setInactiveLeadsDays(
          r.inactive_leads_days ? String(r.inactive_leads_days) : "",
        );
        setRecordingEnabled(
          Boolean(s.recording_enabled ?? lgpd.recording_enabled),
        );
        setRecordingNoticeConfigured(
          Boolean(
            s.recording_notice_configured ?? lgpd.recording_notice_configured,
          ),
        );
      } catch {
        // fallback defaults
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, [activeCrmId]);

  const getToken = async () => {
    const client = getSupabaseClient();
    const { data } = await client.auth.getSession();
    return data?.session?.access_token || null;
  };

  const handleSavePolicy = async () => {
    const numLogs = Number(accessLogsDays);
    if (!Number.isFinite(numLogs) || numLogs < 180) {
      notify(
        "Marco Civil da Internet (Art. 15): o prazo de guarda de logs de acesso não pode ser inferior a 180 dias (6 meses).",
        { type: "warning" },
      );
      return;
    }

    setSaving(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("Sessão expirada.");

      const res = await fetch("/api/retention", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-crm-id": String(activeCrmId),
        },
        body: JSON.stringify({
          action: "update_policy",
          crm_id: activeCrmId,
          access_and_audit_logs_days: numLogs,
          call_recordings_days: callRecordingsDays
            ? Number(callRecordingsDays)
            : null,
          call_transcripts_and_notes_days: transcriptsDays
            ? Number(transcriptsDays)
            : null,
          inactive_leads_days: inactiveLeadsDays
            ? Number(inactiveLeadsDays)
            : null,
          recording_enabled: recordingEnabled,
          recording_notice_configured: recordingNoticeConfigured,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.error?.message || "Erro ao salvar política.");
      }

      notify(
        "Política de Retenção, Marco Civil e Governança de Gravação salva com sucesso!",
        { type: "success" },
      );
    } catch (err: any) {
      notify(err?.message || "Erro ao salvar configurações LGPD.", {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRunRetention = async (dryRun: boolean) => {
    if (
      !dryRun &&
      !window.confirm(
        "Confirma a execução real da rotina de expurgo/anonimização dos dados de negócio vencidos deste CRM?",
      )
    ) {
      return;
    }

    setRunningRoutine(true);
    try {
      const token = await getToken();
      if (!token) throw new Error("Sessão expirada.");

      const res = await fetch("/api/retention", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "x-crm-id": String(activeCrmId),
        },
        body: JSON.stringify({
          action: "run",
          crm_id: activeCrmId,
          dry_run: dryRun,
        }),
      });

      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.error?.message || "Falha na rotina de retenção.");
      }

      setLastReport(json.data);
      notify(json.message, { type: "success" });
    } catch (err: any) {
      notify(err?.message || "Erro ao executar rotina de retenção.", {
        type: "error",
      });
    } finally {
      setRunningRoutine(false);
    }
  };

  return (
    <Card id="lgpd-retention">
      <CardContent className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-muted-foreground flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-500" />
            Privacidade, Retenção & Governança LGPD
          </h2>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Parâmetros de identificação societária, canal do Encarregado de
            Dados (DPO), prazos de retenção (Art. 15 e Art. 16 da Lei
            13.709/2018) e política de chamadas.
          </p>
        </div>

        {/* Identificação Societária e Canal do Encarregado (DPO) */}
        <div className="rounded-xl border border-border/80 bg-muted/20 p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-semibold text-sm text-foreground">
              <Building2 className="w-4 h-4 text-primary" />
              <span>
                Identificação da Empresa & Encarregado de Proteção de Dados
                (DPO)
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="h-7 text-xs gap-1"
              >
                <Link to="/privacidade">
                  <span>Política de Privacidade</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="h-7 text-xs gap-1"
              >
                <Link to="/termos">
                  <span>Termos de Uso</span>
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pt-1 text-xs">
            <div className="p-2.5 rounded-lg bg-background border border-border/60 space-y-1">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                Razão Social & Nome Fantasia
              </span>
              <p className="font-medium text-foreground">
                {LEGAL_COMPANY_INFO.razaoSocial}
              </p>
              <p className="text-[11px] text-muted-foreground">
                CNPJ: {LEGAL_COMPANY_INFO.cnpj}
              </p>
            </div>

            <div className="p-2.5 rounded-lg bg-background border border-border/60 space-y-1">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <MapPin className="w-3 h-3 text-primary" /> Sede & Foro
              </span>
              <p className="text-foreground leading-snug">
                {LEGAL_COMPANY_INFO.enderecoCompleto}
              </p>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Scale className="w-3 h-3 text-muted-foreground" />
                Foro: {LEGAL_COMPANY_INFO.foroComarca}
              </p>
            </div>

            <div className="p-2.5 rounded-lg bg-background border border-border/60 space-y-1">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                <Mail className="w-3 h-3 text-emerald-500" /> Canal DPO &
                Suporte
              </span>
              <p className="text-foreground font-medium">
                {LEGAL_COMPANY_INFO.nomeDpo}
              </p>
              <p className="text-[11px] text-muted-foreground">
                DPO:{" "}
                <a
                  href={`mailto:${LEGAL_COMPANY_INFO.emailDpo}`}
                  className="text-primary hover:underline"
                >
                  {LEGAL_COMPANY_INFO.emailDpo}
                </a>
              </p>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {LEGAL_COMPANY_INFO.telefoneContato}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-1 text-[11px] text-muted-foreground bg-background/50 p-2.5 rounded-lg border border-border/40">
            <span>
              <strong>Prazo Resposta a Titulares:</strong>{" "}
              {LEGAL_COMPANY_INFO.prazoRespostaTitular}
            </span>
            <span>•</span>
            <span>
              <strong>Retenção de Gravações:</strong>{" "}
              {LEGAL_COMPANY_INFO.prazoRetencaoGravacoes}
            </span>
            <span>•</span>
            <span>
              <strong>Exportação pós-cancelamento:</strong>{" "}
              {LEGAL_COMPANY_INFO.prazoExportacaoAposCancelamento}
            </span>
          </div>
        </div>

        <Separator />

        {/* 1. Governança de Gravação de Chamadas e Transcrição por IA */}
        <div className="rounded-xl border border-border/80 bg-muted/30 p-4 space-y-3">
          <div className="flex items-center gap-2 font-semibold text-sm text-foreground">
            <PhoneCall className="w-4 h-4 text-primary" />
            <span>
              Gravação de Chamadas e Transcrição por IA (Desligado por Padrão)
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Por segurança jurídica (Art. 6º, I e III e Art. 7º da LGPD), o
            armazenamento de áudio no Storage e a transcrição via IA (Groq) só
            são processados quando <strong>ambas</strong> as chaves abaixo estão
            ativas:
          </p>

          <div className="space-y-2.5 pt-1">
            <div className="flex items-center justify-between gap-4 p-2.5 rounded-lg bg-background border border-border/60">
              <div>
                <p className="text-xs font-medium text-foreground">
                  1. Aviso prévio de gravação configurado na URA / atendimento
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Declaro que o titular é informado no início da ligação sobre a
                  gravação e finalidade do tratamento.
                </p>
              </div>
              <Switch
                checked={recordingNoticeConfigured}
                onCheckedChange={setRecordingNoticeConfigured}
                disabled={loading}
              />
            </div>

            <div className="flex items-center justify-between gap-4 p-2.5 rounded-lg bg-background border border-border/60">
              <div>
                <p className="text-xs font-medium text-foreground">
                  2. Habilitar armazenamento de áudio e transcrição/resumo por
                  IA
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Quando desativado, apenas a duração e o status da chamada são
                  registrados no CRM.
                </p>
              </div>
              <Switch
                checked={recordingEnabled}
                onCheckedChange={setRecordingEnabled}
                disabled={loading || !recordingNoticeConfigured}
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* 2. Separação entre Logs do Marco Civil e Dados de Negócio */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 font-semibold text-sm text-foreground">
            <Clock className="w-4 h-4 text-primary" />
            <span>
              Tabela de Retenção: Logs de Conexão (Marco Civil) vs. Dados de
              Negócio (LGPD)
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
              <Label className="text-xs font-semibold flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                Logs de Acesso e Auditoria (Mínimo 180 dias - Art. 15 Marco
                Civil)
              </Label>
              <Input
                type="number"
                min={180}
                value={accessLogsDays}
                onChange={(e) => setAccessLogsDays(e.target.value)}
                className="h-8 text-xs bg-background"
              />
              <p className="text-[11px] text-muted-foreground">
                Guarda obrigatória mínima de 6 meses (180 dias) exigida pelo
                Art. 15 da Lei nº 12.965/2014. Nunca sofre expurgo antecipado.
              </p>
            </div>

            <div className="space-y-1.5 p-3 rounded-lg border border-border/80 bg-card">
              <Label className="text-xs font-semibold">
                Retenção de Gravações de Áudio (dias)
              </Label>
              <Input
                type="number"
                min={1}
                placeholder="30 (padrão legal: 30 dias)"
                value={callRecordingsDays}
                onChange={(e) => setCallRecordingsDays(e.target.value)}
                className="h-8 text-xs"
              />
              <p className="text-[11px] text-muted-foreground">
                Padrão contratual:{" "}
                <strong>{LEGAL_COMPANY_INFO.prazoRetencaoGravacoes}</strong>.
                Após este prazo, o arquivo de áudio no bucket{" "}
                <code>call-recordings</code> é removido preservando apenas os
                metadados da chamada.
              </p>
            </div>

            <div className="space-y-1.5 p-3 rounded-lg border border-border/80 bg-card">
              <Label className="text-xs font-semibold">
                Retenção de Transcrições e Notas Antigas (dias)
              </Label>
              <Input
                type="number"
                min={1}
                placeholder="365 (padrão: 365 dias / 1 ano)"
                value={transcriptsDays}
                onChange={(e) => setTranscriptsDays(e.target.value)}
                className="h-8 text-xs"
              />
              <p className="text-[11px] text-muted-foreground">
                Prazo para descarte de anotações operacionais e transcrições
                antigas de contatos.
              </p>
            </div>

            <div className="space-y-1.5 p-3 rounded-lg border border-border/80 bg-card">
              <Label className="text-xs font-semibold">
                Retenção de Leads Inativos (dias sem interação)
              </Label>
              <Input
                type="number"
                min={1}
                placeholder="730 (padrão: 730 dias / 2 anos)"
                value={inactiveLeadsDays}
                onChange={(e) => setInactiveLeadsDays(e.target.value)}
                className="h-8 text-xs"
              />
              <p className="text-[11px] text-muted-foreground">
                Contatos sem interação há mais tempo que este prazo são
                anonimizados de forma irreversível (Art. 15 e 16 da LGPD).
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={runningRoutine}
                onClick={() => handleRunRetention(true)}
                className="h-8 text-xs gap-1.5 cursor-pointer"
              >
                {runningRoutine ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Play className="w-3.5 h-3.5 text-primary" />
                )}
                <span>Simular Expurgo (Dry-Run)</span>
              </Button>

              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={runningRoutine}
                onClick={() => handleRunRetention(false)}
                className="h-8 text-xs gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10 cursor-pointer"
              >
                <span>Executar Expurgo Real Agora</span>
              </Button>
            </div>

            <Button
              type="button"
              size="sm"
              disabled={saving}
              onClick={handleSavePolicy}
              className="h-8 text-xs gap-1.5 cursor-pointer"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              <span>Salvar Política de Retenção</span>
            </Button>
          </div>

          {lastReport && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs space-y-1.5">
              <p className="font-semibold text-foreground">
                Resultado da{" "}
                {lastReport.dry_run ? "Simulação (Dry-Run)" : "Execução Real"}:
              </p>
              <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">
                <li>
                  <strong>Gravações de áudio vencidas:</strong>{" "}
                  {
                    lastReport.business_data_actions?.expired_call_recordings
                      ?.matched_count
                  }{" "}
                  encontradas /{" "}
                  {
                    lastReport.business_data_actions?.expired_call_recordings
                      ?.purged_count
                  }{" "}
                  expurgadas
                </li>
                <li>
                  <strong>Notas/transcrições vencidas:</strong>{" "}
                  {
                    lastReport.business_data_actions
                      ?.expired_notes_and_transcripts?.matched_count
                  }{" "}
                  encontradas /{" "}
                  {
                    lastReport.business_data_actions
                      ?.expired_notes_and_transcripts?.purged_count
                  }{" "}
                  expurgadas
                </li>
                <li>
                  <strong>Leads inativos vencidos:</strong>{" "}
                  {
                    lastReport.business_data_actions?.expired_inactive_leads
                      ?.matched_count
                  }{" "}
                  encontrados /{" "}
                  {
                    lastReport.business_data_actions?.expired_inactive_leads
                      ?.anonymized_count
                  }{" "}
                  anonimizados
                </li>
                <li>
                  <strong>Guarda Marco Civil (Art. 15):</strong>{" "}
                  {lastReport.marco_civil_guard?.status}
                </li>
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
